// Multi-pack icon registry.
//
// Icons are stored as namespaced keys: "lucide:bed-double", "tabler:bath",
// "phosphor:bathtub". The picker writes prefixed keys; older records may
// have a bare lucide kebab name (e.g. "bed"), which we treat as
// "lucide:<name>" for backward compatibility.
//
// The curated default set is imported by name across all three packs so
// only those icons end up in the cleaner UI bundle. The full search
// catalogs for Tabler/Phosphor are dynamic-imported the first time the
// picker opens.

import * as React from "react";
import * as Lucide from "lucide-react";
import { HelpCircle, type LucideIcon } from "lucide-react";

// ---- Curated Tabler icons (verified to exist in installed version) ----
import {
  IconAccessible,
  IconAirConditioning,
  IconAlertOctagon,
  IconAlertTriangle,
  IconArmchair,
  IconArmchair2,
  IconAxe,
  IconBath,
  IconBeach,
  IconBed,
  IconBedFlat,
  IconBlender,
  IconBolt,
  IconBowl,
  IconBowlSpoon,
  IconBread,
  IconBubble,
  IconBubbleTea,
  IconBug,
  IconBugOff,
  IconBuildingCottage,
  IconBuildingPavilion,
  IconCamera,
  IconCandle,
  IconCar,
  IconChefHat,
  IconCheese,
  IconCircleCheck,
  IconCircleX,
  IconClipboardCheck,
  IconClipboardList,
  IconCoffee,
  IconConfetti,
  IconCooker,
  IconDog,
  IconDoor,
  IconDoorEnter,
  IconDoorExit,
  IconDoorOff,
  IconEggs,
  IconElevator,
  IconFlame,
  IconFlameOff,
  IconFlower,
  IconFridge,
  IconGlassFull,
  IconHammer,
  IconHanger,
  IconHotelService,
  IconIroning,
  IconKey,
  IconKeyOff,
  IconLeaf,
  IconListCheck,
  IconMeat,
  IconMicrowave,
  IconPaperBag,
  IconParking,
  IconPaw,
  IconPlant,
  IconPlant2,
  IconPlug,
  IconPlugConnected,
  IconPlugX,
  IconPool,
  IconRecycle,
  IconSalad,
  IconScubaMask,
  IconShoe,
  IconShoppingBag,
  IconShovel,
  IconSmoking,
  IconSmokingNo,
  IconSofa,
  IconSparkles,
  IconStairs,
  IconSwimming,
  IconTeapot,
  IconTool,
  IconTools,
  IconToolsKitchen,
  IconToolsKitchen2,
  IconTrash,
  IconTrashOff,
  IconTree,
  IconTrees,
  IconUmbrella,
  IconUmbrellaOff,
  IconVacuumCleaner,
  IconWalk,
  IconWashMachine,
  IconWheelchair,
  IconWifi,
  IconWifiOff,
  IconWindow,
  IconWindowOff,
} from "@tabler/icons-react";

// ---- Curated Phosphor icons (verified) ----
import {
  Armchair as PhArmchair,
  Bathtub,
  BeachBall,
  Bed as PhBed,
  BeerBottle,
  BeerStein,
  Bread,
  Broom,
  Bug as PhBug,
  BugBeetle,
  BuildingApartment,
  Camera as PhCamera,
  Carrot,
  Chair,
  Champagne,
  ChefHat as PhChefHat,
  CheckCircle,
  Cheese as PhCheese,
  Cherries,
  Cigarette,
  CigaretteSlash,
  ClipboardText,
  Coffee as PhCoffee,
  CoffeeBean,
  CookingPot,
  Couch,
  Door,
  DoorOpen,
  Egg,
  Fan,
  Fingerprint,
  Fire,
  FirstAidKit,
  ForkKnife,
  HairDryer,
  Hamburger,
  Hammer,
  HandSoap,
  HardHat,
  House,
  HouseLine,
  HouseSimple,
  Key as PhKey,
  KeyReturn,
  Knife,
  Ladder,
  Lamp as PhLamp,
  Leaf as PhLeaf,
  Lightbulb,
  LightbulbFilament,
  ListChecks,
  MapPin as PhMapPin,
  OfficeChair,
  Oven,
  PipeWrench,
  Plant,
  Plug,
  PlugCharging,
  PottedPlant,
  Recycle as PhRecycle,
  Screwdriver,
  Shower,
  Snowflake as PhSnowflake,
  Sparkle,
  SprayBottle,
  Stairs as PhStairs,
  Star as PhStar,
  Stool,
  Sun as PhSun,
  SwimmingPool,
  Television,
  TelevisionSimple,
  Thermometer,
  Toilet,
  ToiletPaper,
  Toolbox,
  Tote,
  Towel,
  Trash as PhTrash,
  TrashSimple,
  Tree as PhTree,
  TreePalm,
  TShirt,
  Umbrella as PhUmbrella,
  UmbrellaSimple,
  WarningOctagon,
  Warning as PhWarning,
  WashingMachine,
  Wheelchair,
  WifiHigh,
  WifiSlash,
  Wine as PhWine,
  Wrench as PhWrench,
  XCircle,
} from "@phosphor-icons/react";

// React component type matching what lucide/tabler/phosphor each ship.

type AnyIcon = React.ComponentType<any>;

// Strict component-shape check. Each pack ships its icons as React
// forwardRef objects (or memo-wrapped forwardRefs in some Tabler builds).
// Module namespaces (e.g. Phosphor's `SSR`), context objects (Lucide's
// `LucideProvider`, Phosphor's `IconContext`), plain factory functions
// (Tabler's `createReactComponent`), and TS type re-exports all fail this
// gate — keeping them out of the catalog avoids render-time crashes such
// as "iconNode.map is not a function" and "Element type is invalid".
const REACT_FORWARD_REF = Symbol.for("react.forward_ref");
const REACT_MEMO = Symbol.for("react.memo");
function isIconComponent(v: unknown): v is AnyIcon {
  if (!v || typeof v !== "object") return false;

  const sy = (v as any).$$typeof;
  return sy === REACT_FORWARD_REF || sy === REACT_MEMO;
}
function warnSkipped(pack: Pack, name: string) {
  if (process.env.NODE_ENV !== "production") {

    console.warn(`[icon-registry] skipped non-component export: ${pack}:${name}`);
  }
}

export type Pack = "lucide" | "tabler" | "phosphor";
export interface IconRecord {
  /** Namespaced key, e.g. "lucide:bed-double". */
  key: string;
  Icon: AnyIcon;
  pack: Pack;
  /** Lowercase searchable string. */
  keywords: string;
}

// ----- key helpers -----
function lucideExportToId(name: string): string {
  return name
    .replace(/^Lucide/, "")
    .replace(/Icon$/, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
}

function tablerExportToId(name: string): string {
  // IconBath2 -> "bath-2", IconWashTumbleOff -> "wash-tumble-off"
  return name
    .replace(/^Icon/, "")
    .replace(/([a-z])([A-Z])/g, "$1-$2")
    .replace(/([A-Z])(\d)/g, "$1-$2")
    .toLowerCase();
}

function phosphorExportToId(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .toLowerCase();
}

function namespacedKey(pack: Pack, id: string): string {
  return `${pack}:${id}`;
}

export function parseKey(key: string | null | undefined): { pack: Pack; id: string } | null {
  if (!key || typeof key !== "string") return null;
  // Modern: "<pack>:<id>"
  const colon = /^(lucide|tabler|phosphor):(.+)$/.exec(key);
  if (colon) return { pack: colon[1] as Pack, id: colon[2] };
  // Legacy variant: some rows used a dash separator, e.g. "lucide-bed-double".
  // Only treat as namespaced when the prefix is one of our packs AND something
  // follows. Otherwise fall through to the bare-lucide-kebab legacy path.
  const dash = /^(lucide|tabler|phosphor)-(.+)$/.exec(key);
  if (dash) return { pack: dash[1] as Pack, id: dash[2] };
  // Legacy bare-string: treat as lucide kebab id.
  return { pack: "lucide", id: key };
}

/**
 * Convert a stored / namespaced icon key into a human-friendly label for
 * trigger buttons and tooltips. Examples:
 *   "tabler:bath"          -> "Bath"
 *   "tabler:bath-2"        -> "Bath 2"
 *   "phosphor:bathtub"     -> "Bathtub"
 *   "lucide:bed-double"    -> "Bed Double"
 *   "lucide:sparkles"      -> "Sparkles"
 *   "Sparkles"             -> "Sparkles" (legacy bare lucide PascalCase)
 *   "bed-double"           -> "Bed Double" (legacy bare lucide kebab)
 */
export function prettifyIconName(input: string | null | undefined): string {
  if (!input || typeof input !== "string") return "";
  // Strip pack prefix if present.
  const colon = input.indexOf(":");
  let id = colon >= 0 ? input.slice(colon + 1) : input;
  // Strip a leading "Icon" left over from a raw tabler export name.
  if (/^Icon[A-Z]/.test(id)) id = id.slice(4);
  // If it's a single PascalCase/CamelCase token (no separators), leave it.
  if (!/[-_\s]/.test(id) && /^[A-Za-z][A-Za-z0-9]*$/.test(id) && /[A-Z]/.test(id)) {
    return id;
  }
  // Otherwise split on -, _, whitespace, or camelCase boundaries and Title-Case.
  const parts = id
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[-_\s]+/)
    .filter(Boolean);
  if (parts.length === 0) return id;
  return parts.map((p) => (p ? p[0].toUpperCase() + p.slice(1).toLowerCase() : "")).join(" ");
}

// Normalize a stored id into the variants we want to try when looking up
// against either curated or dynamic catalogs. The picker writes kebab form
// (e.g. "thermometer-hot"), but we also try PascalCase forms in case a row
// somehow stored the raw export name.
function normalizeIdVariants(id: string): string[] {
  const variants = new Set<string>();
  variants.add(id);
  // kebab -> kebab is identity
  // PascalCase -> kebab
  if (/[A-Z]/.test(id)) {
    variants.add(
      id
        .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
        .replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
        .toLowerCase(),
    );
  }
  // snake -> kebab
  if (id.includes("_")) variants.add(id.replace(/_/g, "-").toLowerCase());
  return Array.from(variants);
}

// ---------- Curated set ----------
// Hand-picked from the three packs. ~150–200 STR-relevant icons.
const CURATED_LUCIDE: Array<{ id: string; Comp: LucideIcon }> = (() => {
  // List of lucide PascalCase exports we want in curated.
  const names = [
    // Rooms / spaces
    "Bed", "BedDouble", "BedSingle", "Bath", "ShowerHead", "Sofa", "Armchair",
    "Lamp", "LampFloor", "DoorOpen", "DoorClosed", "House", "Home", "Building",
    "KeyRound", "KeySquare", "Tent", "Umbrella", "Palmtree", "TreePine",
    "TreeDeciduous", "Flower", "Flower2",
    // Kitchen
    "Microwave", "Refrigerator", "Coffee", "Beer", "Wine", "WineOff",
    "CupSoda", "Utensils", "UtensilsCrossed", "CookingPot", "ChefHat",
    "Soup", "Salad", "Apple", "Egg",
    // Cleaning / consumables
    "Trash", "Trash2", "Recycle", "WashingMachine", "SprayCan", "Sparkles",
    "Tornado", "Wind", "Droplet", "Droplets",
    // Outdoor / property
    "Flame", "Lock", "LockKeyhole", "Unlock", "Wifi", "WifiOff",
    "Tv", "Cable", "Monitor", "Airplay", "Phone", "Package", "Package2",
    "Sun", "Snowflake", "Thermometer", "AirVent", "Fan",
    // Status / maintenance
    "AlertTriangle", "AlertOctagon", "AlertCircle", "Wrench", "Hammer",
    "PaintRoller", "PaintBucket", "Paintbrush", "PaintbrushVertical",
    "Bandage", "Bug", "BugOff", "Cigarette", "CigaretteOff", "Scissors",
    "Plug", "PlugZap",
    // Generic
    "Check", "CheckCircle2", "CircleCheck", "CircleAlert", "X", "XCircle",
    "Dot", "Image", "Camera", "Mic", "Clipboard", "ClipboardCheck",
    "ListChecks", "Ban", "Eye", "EyeOff", "Star", "Heart", "Bell", "Search",
    "Filter", "Settings",
  ];

  const map = Lucide as unknown as Record<string, any>;
  const seen = new Set<string>();
  const out: Array<{ id: string; Comp: LucideIcon }> = [];
  for (const n of names) {
    const Comp = map[n];
    if (!Comp || (typeof Comp !== "function" && typeof Comp !== "object")) continue;
    const id = lucideExportToId(n);
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ id, Comp: Comp as LucideIcon });
  }
  return out;
})();

const CURATED_TABLER: Array<{ name: string; Comp: AnyIcon }> = [
  { name: "IconAccessible", Comp: IconAccessible },
  { name: "IconAirConditioning", Comp: IconAirConditioning },
  { name: "IconAlertOctagon", Comp: IconAlertOctagon },
  { name: "IconAlertTriangle", Comp: IconAlertTriangle },
  { name: "IconArmchair", Comp: IconArmchair },
  { name: "IconArmchair2", Comp: IconArmchair2 },
  { name: "IconAxe", Comp: IconAxe },
  { name: "IconBath", Comp: IconBath },
  { name: "IconBeach", Comp: IconBeach },
  { name: "IconBed", Comp: IconBed },
  { name: "IconBedFlat", Comp: IconBedFlat },
  { name: "IconBlender", Comp: IconBlender },
  { name: "IconBolt", Comp: IconBolt },
  { name: "IconBowl", Comp: IconBowl },
  { name: "IconBowlSpoon", Comp: IconBowlSpoon },
  { name: "IconBread", Comp: IconBread },
  { name: "IconBubble", Comp: IconBubble },
  { name: "IconBubbleTea", Comp: IconBubbleTea },
  { name: "IconBug", Comp: IconBug },
  { name: "IconBugOff", Comp: IconBugOff },
  { name: "IconBuildingCottage", Comp: IconBuildingCottage },
  { name: "IconBuildingPavilion", Comp: IconBuildingPavilion },
  { name: "IconCamera", Comp: IconCamera },
  { name: "IconCandle", Comp: IconCandle },
  { name: "IconCar", Comp: IconCar },
  { name: "IconChefHat", Comp: IconChefHat },
  { name: "IconCheese", Comp: IconCheese },
  { name: "IconCircleCheck", Comp: IconCircleCheck },
  { name: "IconCircleX", Comp: IconCircleX },
  { name: "IconClipboardCheck", Comp: IconClipboardCheck },
  { name: "IconClipboardList", Comp: IconClipboardList },
  { name: "IconCoffee", Comp: IconCoffee },
  { name: "IconConfetti", Comp: IconConfetti },
  { name: "IconCooker", Comp: IconCooker },
  { name: "IconDog", Comp: IconDog },
  { name: "IconDoor", Comp: IconDoor },
  { name: "IconDoorEnter", Comp: IconDoorEnter },
  { name: "IconDoorExit", Comp: IconDoorExit },
  { name: "IconDoorOff", Comp: IconDoorOff },
  { name: "IconEggs", Comp: IconEggs },
  { name: "IconElevator", Comp: IconElevator },
  { name: "IconFlame", Comp: IconFlame },
  { name: "IconFlameOff", Comp: IconFlameOff },
  { name: "IconFlower", Comp: IconFlower },
  { name: "IconFridge", Comp: IconFridge },
  { name: "IconGlassFull", Comp: IconGlassFull },
  { name: "IconHammer", Comp: IconHammer },
  { name: "IconHanger", Comp: IconHanger },
  { name: "IconHotelService", Comp: IconHotelService },
  { name: "IconIroning", Comp: IconIroning },
  { name: "IconKey", Comp: IconKey },
  { name: "IconKeyOff", Comp: IconKeyOff },
  { name: "IconLeaf", Comp: IconLeaf },
  { name: "IconListCheck", Comp: IconListCheck },
  { name: "IconMeat", Comp: IconMeat },
  { name: "IconMicrowave", Comp: IconMicrowave },
  { name: "IconPaperBag", Comp: IconPaperBag },
  { name: "IconParking", Comp: IconParking },
  { name: "IconPaw", Comp: IconPaw },
  { name: "IconPlant", Comp: IconPlant },
  { name: "IconPlant2", Comp: IconPlant2 },
  { name: "IconPlug", Comp: IconPlug },
  { name: "IconPlugConnected", Comp: IconPlugConnected },
  { name: "IconPlugX", Comp: IconPlugX },
  { name: "IconPool", Comp: IconPool },
  { name: "IconRecycle", Comp: IconRecycle },
  { name: "IconSalad", Comp: IconSalad },
  { name: "IconScubaMask", Comp: IconScubaMask },
  { name: "IconShoe", Comp: IconShoe },
  { name: "IconShoppingBag", Comp: IconShoppingBag },
  { name: "IconShovel", Comp: IconShovel },
  { name: "IconSmoking", Comp: IconSmoking },
  { name: "IconSmokingNo", Comp: IconSmokingNo },
  { name: "IconSofa", Comp: IconSofa },
  { name: "IconSparkles", Comp: IconSparkles },
  { name: "IconStairs", Comp: IconStairs },
  { name: "IconSwimming", Comp: IconSwimming },
  { name: "IconTeapot", Comp: IconTeapot },
  { name: "IconTool", Comp: IconTool },
  { name: "IconTools", Comp: IconTools },
  { name: "IconToolsKitchen", Comp: IconToolsKitchen },
  { name: "IconToolsKitchen2", Comp: IconToolsKitchen2 },
  { name: "IconTrash", Comp: IconTrash },
  { name: "IconTrashOff", Comp: IconTrashOff },
  { name: "IconTree", Comp: IconTree },
  { name: "IconTrees", Comp: IconTrees },
  { name: "IconUmbrella", Comp: IconUmbrella },
  { name: "IconUmbrellaOff", Comp: IconUmbrellaOff },
  { name: "IconVacuumCleaner", Comp: IconVacuumCleaner },
  { name: "IconWalk", Comp: IconWalk },
  { name: "IconWashMachine", Comp: IconWashMachine },
  { name: "IconWheelchair", Comp: IconWheelchair },
  { name: "IconWifi", Comp: IconWifi },
  { name: "IconWifiOff", Comp: IconWifiOff },
  { name: "IconWindow", Comp: IconWindow },
  { name: "IconWindowOff", Comp: IconWindowOff },
];

const CURATED_PHOSPHOR: Array<{ name: string; Comp: AnyIcon }> = [
  { name: "Armchair", Comp: PhArmchair },
  { name: "Bathtub", Comp: Bathtub },
  { name: "BeachBall", Comp: BeachBall },
  { name: "Bed", Comp: PhBed },
  { name: "BeerBottle", Comp: BeerBottle },
  { name: "BeerStein", Comp: BeerStein },
  { name: "Bread", Comp: Bread },
  { name: "Broom", Comp: Broom },
  { name: "Bug", Comp: PhBug },
  { name: "BugBeetle", Comp: BugBeetle },
  { name: "BuildingApartment", Comp: BuildingApartment },
  { name: "Camera", Comp: PhCamera },
  { name: "Carrot", Comp: Carrot },
  { name: "Chair", Comp: Chair },
  { name: "Champagne", Comp: Champagne },
  { name: "ChefHat", Comp: PhChefHat },
  { name: "CheckCircle", Comp: CheckCircle },
  { name: "Cheese", Comp: PhCheese },
  { name: "Cherries", Comp: Cherries },
  { name: "Cigarette", Comp: Cigarette },
  { name: "CigaretteSlash", Comp: CigaretteSlash },
  { name: "ClipboardText", Comp: ClipboardText },
  { name: "Coffee", Comp: PhCoffee },
  { name: "CoffeeBean", Comp: CoffeeBean },
  { name: "CookingPot", Comp: CookingPot },
  { name: "Couch", Comp: Couch },
  { name: "Door", Comp: Door },
  { name: "DoorOpen", Comp: DoorOpen },
  { name: "Egg", Comp: Egg },
  { name: "Fan", Comp: Fan },
  { name: "Fingerprint", Comp: Fingerprint },
  { name: "Fire", Comp: Fire },
  { name: "FirstAidKit", Comp: FirstAidKit },
  { name: "ForkKnife", Comp: ForkKnife },
  { name: "HairDryer", Comp: HairDryer },
  { name: "Hamburger", Comp: Hamburger },
  { name: "Hammer", Comp: Hammer },
  { name: "HandSoap", Comp: HandSoap },
  { name: "HardHat", Comp: HardHat },
  { name: "House", Comp: House },
  { name: "HouseLine", Comp: HouseLine },
  { name: "HouseSimple", Comp: HouseSimple },
  { name: "Key", Comp: PhKey },
  { name: "KeyReturn", Comp: KeyReturn },
  { name: "Knife", Comp: Knife },
  { name: "Ladder", Comp: Ladder },
  { name: "Lamp", Comp: PhLamp },
  { name: "Leaf", Comp: PhLeaf },
  { name: "Lightbulb", Comp: Lightbulb },
  { name: "LightbulbFilament", Comp: LightbulbFilament },
  { name: "ListChecks", Comp: ListChecks },
  { name: "MapPin", Comp: PhMapPin },
  { name: "OfficeChair", Comp: OfficeChair },
  { name: "Oven", Comp: Oven },
  { name: "PipeWrench", Comp: PipeWrench },
  { name: "Plant", Comp: Plant },
  { name: "Plug", Comp: Plug },
  { name: "PlugCharging", Comp: PlugCharging },
  { name: "PottedPlant", Comp: PottedPlant },
  { name: "Recycle", Comp: PhRecycle },
  { name: "Screwdriver", Comp: Screwdriver },
  { name: "Shower", Comp: Shower },
  { name: "Snowflake", Comp: PhSnowflake },
  { name: "Sparkle", Comp: Sparkle },
  { name: "SprayBottle", Comp: SprayBottle },
  { name: "Stairs", Comp: PhStairs },
  { name: "Star", Comp: PhStar },
  { name: "Stool", Comp: Stool },
  { name: "Sun", Comp: PhSun },
  { name: "SwimmingPool", Comp: SwimmingPool },
  { name: "Television", Comp: Television },
  { name: "TelevisionSimple", Comp: TelevisionSimple },
  { name: "Thermometer", Comp: Thermometer },
  { name: "Toilet", Comp: Toilet },
  { name: "ToiletPaper", Comp: ToiletPaper },
  { name: "Toolbox", Comp: Toolbox },
  { name: "Tote", Comp: Tote },
  { name: "Towel", Comp: Towel },
  { name: "Trash", Comp: PhTrash },
  { name: "TrashSimple", Comp: TrashSimple },
  { name: "Tree", Comp: PhTree },
  { name: "TreePalm", Comp: TreePalm },
  { name: "TShirt", Comp: TShirt },
  { name: "Umbrella", Comp: PhUmbrella },
  { name: "UmbrellaSimple", Comp: UmbrellaSimple },
  { name: "Warning", Comp: PhWarning },
  { name: "WarningOctagon", Comp: WarningOctagon },
  { name: "WashingMachine", Comp: WashingMachine },
  { name: "Wheelchair", Comp: Wheelchair },
  { name: "WifiHigh", Comp: WifiHigh },
  { name: "WifiSlash", Comp: WifiSlash },
  { name: "Wine", Comp: PhWine },
  { name: "Wrench", Comp: PhWrench },
  { name: "XCircle", Comp: XCircle },
];

// Curated registry — assembled eagerly because the components are imported
// by name above. This is what shows in the picker default view, and is also
// used as the synchronous lookup for rendering.
const CURATED: IconRecord[] = (() => {
  const out: IconRecord[] = [];
  for (const { id, Comp } of CURATED_LUCIDE) {
    out.push({
      key: namespacedKey("lucide", id),
      Icon: Comp,
      pack: "lucide",
      keywords: id.replace(/-/g, " "),
    });
  }
  for (const { name, Comp } of CURATED_TABLER) {
    const id = tablerExportToId(name);
    out.push({
      key: namespacedKey("tabler", id),
      Icon: Comp,
      pack: "tabler",
      keywords: id.replace(/-/g, " "),
    });
  }
  for (const { name, Comp } of CURATED_PHOSPHOR) {
    const id = phosphorExportToId(name);
    out.push({
      key: namespacedKey("phosphor", id),
      Icon: Comp,
      pack: "phosphor",
      keywords: id.replace(/-/g, " "),
    });
  }
  return out;
})();

export function getCurated(): IconRecord[] {
  return CURATED;
}

// Dev-only: warn at module load if any curated entry failed to bind to a real
// component (catches drift when icon packs version-bump).
if (process.env.NODE_ENV !== "production") {
  for (const r of CURATED) {
    if (!r.Icon || (typeof r.Icon !== "function" && typeof r.Icon !== "object")) {

      console.warn(`[icon-registry] curated entry doesn't resolve: ${r.key}`);
    }
  }
}

// Synchronous lookup table for resolveIcon. Built from CURATED + lucide-full
// (lucide is statically imported so it's free). For tabler/phosphor names not
// in the curated set, resolveIcon falls back to HelpCircle until the dynamic
// full catalog has been loaded (after the picker has been opened once).
const CURATED_BY_KEY: Map<string, IconRecord> = new Map(CURATED.map((r) => [r.key, r]));

// Build the full lucide map lazily but synchronously (just a one-time scan of
// the already-imported module).
let LUCIDE_FULL_BY_ID: Map<string, AnyIcon> | null = null;
function getLucideFullById(): Map<string, AnyIcon> {
  if (LUCIDE_FULL_BY_ID) return LUCIDE_FULL_BY_ID;
  const m = new Map<string, AnyIcon>();

  const map = Lucide as unknown as Record<string, any>;
  const seenComp = new Map<unknown, string>();
  for (const name of Object.keys(map)) {
    if (!/^[A-Z]/.test(name)) continue;
    const Comp = map[name];
    if (!isIconComponent(Comp)) {
      if (Comp !== undefined) warnSkipped("lucide", name);
      continue;
    }
    const id = lucideExportToId(name);
    // Prefer shortest export name for canonical id.
    const prev = seenComp.get(Comp);
    if (prev && prev.length <= name.length) continue;
    seenComp.set(Comp, name);
    m.set(id, Comp);
  }
  LUCIDE_FULL_BY_ID = m;
  return m;
}

// After the picker (or a resolveIcon miss) dynamically loads the full
// Tabler/Phosphor catalogs they register here so subsequent resolveIcon()
// calls find them synchronously.
const DYNAMIC_BY_KEY: Map<string, AnyIcon> = new Map();

// Pub-sub for the <Icon /> component so it can re-render once the dynamic
// catalog finishes loading after a first-paint miss.
const catalogListeners = new Set<() => void>();
let catalogVersion = 0;
function notifyCatalog() {
  catalogVersion += 1;
  for (const fn of catalogListeners) fn();
}
function subscribeCatalog(fn: () => void): () => void {
  catalogListeners.add(fn);
  return () => catalogListeners.delete(fn);
}
function getCatalogVersion() {
  return catalogVersion;
}

export function registerDynamicIcons(records: Iterable<{ key: string; Icon: AnyIcon }>) {
  let added = 0;
  for (const r of records) {
    if (!DYNAMIC_BY_KEY.has(r.key)) {
      DYNAMIC_BY_KEY.set(r.key, r.Icon);
      added += 1;
    }
  }
  if (added > 0) notifyCatalog();
}

// Throttle re-entrant load attempts.
let bgLoadInflight = false;
function kickOffBackgroundLoad() {
  if (bgLoadInflight) return;
  if (typeof window === "undefined") return; // server: skip
  bgLoadInflight = true;
  loadFullCatalog()
    .then(() => notifyCatalog())
    .catch(() => {})
    .finally(() => {
      bgLoadInflight = false;
    });
}

function tryResolveSync(parsed: { pack: Pack; id: string }): AnyIcon | null {
  for (const variant of normalizeIdVariants(parsed.id)) {
    const namespaced = namespacedKey(parsed.pack, variant);
    const curated = CURATED_BY_KEY.get(namespaced);
    if (curated) return curated.Icon;
    if (parsed.pack === "lucide") {
      const comp = getLucideFullById().get(variant);
      if (comp) return comp;
    }
    const dyn = DYNAMIC_BY_KEY.get(namespaced);
    if (dyn) return dyn;
  }
  return null;
}

// Resilient fallback in case `HelpCircle` is somehow tree-shaken away in a
// downstream consumer. We never want resolveIcon to return undefined.
const FALLBACK_ICON: AnyIcon = (HelpCircle ?? (function NoopIcon() {
  return React.createElement("svg", { width: 16, height: 16, viewBox: "0 0 16 16" });
})) as AnyIcon;

/**
 * Synchronous lookup. May return the fallback on the first call for a
 * non-curated tabler/phosphor key — in that case it triggers a background
 * catalog load so the next render (or any <Icon /> subscriber) gets the
 * real component.
 *
 * Accepts new prefixed keys ("lucide:bed", "tabler:bath", "phosphor:bathtub"),
 * legacy dash-prefixed keys ("lucide-bed-double"), and legacy bare lucide
 * kebab ids ("bed", "shower-head").
 *
 * ALWAYS returns a renderable component (never undefined). Falls back to
 * Lucide's `HelpCircle` (and, in the unlikely event that is also missing,
 * a no-op SVG placeholder) so consumers can safely render `<Icon ... />`
 * without an `if (!Icon)` guard.
 */
export function resolveIcon(key: string | null | undefined): AnyIcon {
  const parsed = parseKey(key);
  if (!parsed) return FALLBACK_ICON;
  const hit = tryResolveSync(parsed);
  if (hit) return hit;
  if (parsed.pack !== "lucide") kickOffBackgroundLoad();
  if (process.env.NODE_ENV !== "production") {

    console.warn("[icon-registry] missing icon for key:", key);
  }
  return FALLBACK_ICON;
}

/**
 * React component for rendering a stored icon key. Resolves synchronously
 * when possible; otherwise renders a placeholder, triggers the full catalog
 * load in the background, and re-renders when it resolves.
 *
 * Prefer this over calling resolveIcon() directly in render paths so first
 * paint corrects itself for non-curated keys (e.g. user-picked
 * "phosphor:thermometer-hot" from the full-search catalog).
 */
export interface IconProps extends Omit<React.SVGAttributes<SVGElement>, "name"> {
  name: string | null | undefined;
  size?: number | string;
}
export const Icon = React.forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, ...rest },
  ref,
) {
  // Subscribe to catalog updates so we re-render when dynamic icons land.
  React.useSyncExternalStore(subscribeCatalog, getCatalogVersion, getCatalogVersion);
  const parsed = parseKey(name);
  let Comp: AnyIcon = FALLBACK_ICON;
  if (parsed) {
    const hit = tryResolveSync(parsed);
    if (hit) {
      Comp = hit;
    } else if (parsed.pack !== "lucide") {
      kickOffBackgroundLoad();
    }
  }

  return <Comp ref={ref as any} {...rest} />;
});

/**
 * Build the full searchable catalog (curated + lucide-full + dynamic-loaded
 * Tabler + dynamic-loaded Phosphor). Tabler/Phosphor are dynamic-imported on
 * first call to keep them out of the main bundle.
 *
 * Returns a stable array sorted by key.
 */
let FULL_PROMISE: Promise<IconRecord[]> | null = null;
export function loadFullCatalog(): Promise<IconRecord[]> {
  if (FULL_PROMISE) return FULL_PROMISE;
  FULL_PROMISE = (async () => {
    const records: IconRecord[] = [];
    const seenKeys = new Set<string>();

    // Lucide full (already imported statically).
    const lucideById = getLucideFullById();
    for (const [id, Comp] of lucideById) {
      const key = namespacedKey("lucide", id);
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      records.push({ key, Icon: Comp, pack: "lucide", keywords: id.replace(/-/g, " ") });
    }

    // Tabler full — dynamic import.
    // Tabler icons are forwardRef components produced by createReactComponent.
    // Require the Icon* name prefix AND a forwardRef/memo shape so factory
    // helpers like `createReactComponent` (a plain function) and type-only
    // re-exports are excluded. Admitting them causes
    // "iconNode.map is not a function" at render time.
    try {
      const Tabler = (await import("@tabler/icons-react")) as unknown as Record<string, unknown>;
      const seenComp = new Map<unknown, string>();
      for (const name of Object.keys(Tabler)) {
        if (!name.startsWith("Icon")) continue;
        const Comp = Tabler[name];
        if (!isIconComponent(Comp)) {
          if (Comp !== undefined) warnSkipped("tabler", name);
          continue;
        }
        const prev = seenComp.get(Comp);
        if (prev && prev.length <= name.length) continue;
        seenComp.set(Comp, name);
        const id = tablerExportToId(name);
        const key = namespacedKey("tabler", id);
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        records.push({ key, Icon: Comp, pack: "tabler", keywords: id.replace(/-/g, " ") });
        if (!DYNAMIC_BY_KEY.has(key)) DYNAMIC_BY_KEY.set(key, Comp);
      }
    } catch {
      // ignore; full search will just miss Tabler.
    }

    // Phosphor full — dynamic import.
    // Require forwardRef/memo shape. This rejects module namespaces like
    // `SSR` (typeof === "object" but no $$typeof) which would otherwise be
    // admitted and crash React with "Element type is invalid". Also
    // implicitly drops `IconContext` (a react.context) and the abstract
    // `IconBase` primitive.
    try {
      const Phosphor = (await import("@phosphor-icons/react")) as unknown as Record<string, unknown>;
      const seenComp = new Map<unknown, string>();
      for (const name of Object.keys(Phosphor)) {
        if (!/^[A-Z]/.test(name)) continue;
        if (name === "IconBase") continue; // abstract primitive, not a real icon
        const Comp = Phosphor[name];
        if (!isIconComponent(Comp)) {
          if (Comp !== undefined) warnSkipped("phosphor", name);
          continue;
        }
        const prev = seenComp.get(Comp);
        if (prev && prev.length <= name.length) continue;
        seenComp.set(Comp, name);
        const id = phosphorExportToId(name);
        const key = namespacedKey("phosphor", id);
        if (seenKeys.has(key)) continue;
        seenKeys.add(key);
        records.push({ key, Icon: Comp, pack: "phosphor", keywords: id.replace(/-/g, " ") });
        if (!DYNAMIC_BY_KEY.has(key)) DYNAMIC_BY_KEY.set(key, Comp);
      }
    } catch {
      // ignore.
    }

    records.sort((a, b) => a.key.localeCompare(b.key));
    return records;
  })();
  return FULL_PROMISE;
}

// ---------- Keyword aliases & fuzzy search ----------
//
// Aliases are keyed by the bare icon id (the part after the `pack:` prefix),
// so they apply across packs when the same id appears (e.g. lucide "bath",
// tabler "bath", phosphor "bath" all share the same aliases). Curated set
// only — full-catalog matches still fall back to name substring scoring.

const KEYWORD_ALIASES: Record<string, string[]> = {
  // Bathroom
  "bath": ["bathroom", "tub", "shower", "wash"],
  "bathtub": ["bath", "tub", "bathroom", "wash"],
  "shower": ["bath", "bathroom", "wash"],
  "shower-head": ["shower", "bath", "bathroom", "wash"],
  "toilet": ["bathroom", "wc", "restroom"],
  "toilet-paper": ["bathroom", "tp", "supplies"],
  "towel": ["bath", "bathroom", "linen"],
  "hand-soap": ["soap", "wash", "bathroom", "kitchen"],
  "hair-dryer": ["bathroom", "dryer", "hair"],
  // Bedroom
  "bed": ["bedroom", "sleep", "furniture"],
  "bed-double": ["bedroom", "king", "queen", "sleep", "furniture"],
  "bed-single": ["bedroom", "single", "sleep", "furniture"],
  "bed-flat": ["bedroom", "sleep", "furniture"],
  // Living / furniture
  "sofa": ["couch", "living", "living room", "furniture", "seat"],
  "couch": ["sofa", "living", "living room", "furniture", "seat"],
  "armchair": ["chair", "living", "furniture", "seat"],
  "armchair-2": ["chair", "living", "furniture", "seat"],
  "chair": ["seat", "furniture"],
  "office-chair": ["chair", "desk", "office", "furniture"],
  "stool": ["seat", "furniture"],
  "lamp": ["light", "lighting", "living"],
  "lamp-floor": ["light", "lighting", "living"],
  "lightbulb": ["light", "lighting", "bulb"],
  "lightbulb-filament": ["light", "lighting", "bulb"],
  // Doors / access
  "door": ["entrance", "exit", "entry"],
  "door-open": ["entrance", "exit", "entry", "open"],
  "door-closed": ["entrance", "exit", "closed"],
  "door-enter": ["entrance", "entry", "enter"],
  "door-exit": ["exit", "leave"],
  "door-off": ["closed", "blocked"],
  "key": ["lock", "unlock", "access", "door"],
  "key-round": ["lock", "access", "door"],
  "key-square": ["lock", "access", "door"],
  "key-return": ["lock", "access", "checkout"],
  "key-off": ["locked", "blocked", "no access"],
  "lock": ["secure", "key", "access"],
  "lock-keyhole": ["secure", "key", "access"],
  "unlock": ["open", "access", "key"],
  "fingerprint": ["access", "secure", "lock", "biometric"],
  // Kitchen
  "refrigerator": ["fridge", "kitchen", "appliance", "cold"],
  "fridge": ["refrigerator", "kitchen", "appliance", "cold"],
  "oven": ["stove", "cooker", "kitchen", "appliance", "bake"],
  "cooker": ["stove", "oven", "kitchen", "appliance"],
  "microwave": ["kitchen", "appliance", "heat"],
  "blender": ["kitchen", "appliance", "smoothie"],
  "cooking-pot": ["pot", "kitchen", "cook"],
  "chef-hat": ["cook", "kitchen", "chef"],
  "utensils": ["fork", "knife", "kitchen", "dining"],
  "utensils-crossed": ["fork", "knife", "kitchen", "dining", "restaurant"],
  "fork-knife": ["utensils", "kitchen", "dining"],
  "knife": ["utensils", "kitchen", "blade"],
  "tools-kitchen": ["utensils", "kitchen"],
  "tools-kitchen-2": ["utensils", "kitchen"],
  "coffee": ["caffeine", "morning", "kitchen", "drink"],
  "coffee-bean": ["coffee", "kitchen", "drink"],
  "teapot": ["tea", "kitchen", "drink"],
  "cup-soda": ["drink", "soda", "cup"],
  "wine": ["drink", "alcohol", "bar"],
  "wine-off": ["no alcohol", "no drinks"],
  "beer": ["drink", "alcohol", "bar"],
  "beer-bottle": ["beer", "drink", "alcohol"],
  "beer-stein": ["beer", "drink", "alcohol"],
  "champagne": ["drink", "alcohol", "celebration"],
  "glass-full": ["drink", "cup"],
  "bowl": ["kitchen", "dish"],
  "bowl-spoon": ["kitchen", "dish", "soup"],
  "soup": ["bowl", "kitchen", "food"],
  "salad": ["food", "kitchen", "healthy"],
  "bread": ["food", "kitchen", "bakery"],
  "egg": ["food", "kitchen", "breakfast"],
  "eggs": ["food", "kitchen", "breakfast"],
  "apple": ["food", "fruit", "kitchen"],
  "carrot": ["food", "vegetable", "kitchen"],
  "cheese": ["food", "dairy", "kitchen"],
  "cherries": ["food", "fruit", "kitchen"],
  "meat": ["food", "protein", "kitchen"],
  "hamburger": ["food", "burger", "kitchen"],
  "pizza": ["food", "kitchen"],
  "croissant": ["food", "bakery", "breakfast"],
  "paper-bag": ["grocery", "kitchen", "supplies"],
  "shopping-bag": ["grocery", "supplies"],
  // Cleaning
  "trash": ["garbage", "waste", "rubbish", "bin", "litter"],
  "trash-2": ["garbage", "waste", "rubbish", "bin"],
  "trash-simple": ["garbage", "waste", "bin"],
  "trash-off": ["no garbage", "no waste"],
  "recycle": ["recycling", "green", "bin"],
  "sparkles": ["clean", "shine", "magic", "fresh"],
  "sparkle": ["clean", "shine", "magic", "fresh"],
  "broom": ["sweep", "clean", "floor"],
  "spray-can": ["cleaning", "spray", "supplies"],
  "spray-bottle": ["cleaning", "spray", "supplies"],
  "washing-machine": ["laundry", "wash", "appliance"],
  "wash-machine": ["laundry", "wash", "appliance"],
  "vacuum-cleaner": ["vacuum", "clean", "floor"],
  "hanger": ["closet", "laundry", "clothes"],
  "ironing": ["iron", "laundry", "clothes"],
  "t-shirt": ["clothes", "laundry"],
  "shoe": ["footwear", "outdoor"],
  // Outdoors / nature
  "leaf": ["plant", "nature", "green", "garden"],
  "flower": ["plant", "nature", "garden"],
  "flower-2": ["plant", "nature", "garden"],
  "plant": ["nature", "garden", "green"],
  "plant-2": ["nature", "garden", "green"],
  "potted-plant": ["plant", "garden", "indoor"],
  "tree": ["plant", "nature", "outdoor"],
  "trees": ["forest", "plant", "nature", "outdoor"],
  "tree-pine": ["tree", "outdoor", "plant", "garden", "forest"],
  "tree-deciduous": ["tree", "outdoor", "plant", "garden"],
  "palmtree": ["beach", "tropical", "outdoor", "tree", "palm"],
  "palm-tree": ["beach", "tropical", "outdoor", "tree", "palm"],
  "tree-palm": ["beach", "tropical", "outdoor", "tree", "palm"],
  "sprout": ["plant", "garden", "grow"],
  "fence": ["yard", "garden", "outdoor"],
  "beach": ["sand", "ocean", "outdoor", "tropical"],
  "beach-ball": ["beach", "outdoor", "play"],
  "umbrella": ["rain", "beach", "weather", "outdoor"],
  "umbrella-simple": ["rain", "beach", "weather", "outdoor"],
  "umbrella-off": ["no rain", "no umbrella"],
  "pool": ["swimming", "water", "outdoor"],
  "swimming": ["pool", "water", "outdoor"],
  "swimming-pool": ["pool", "water", "outdoor"],
  "scuba-mask": ["snorkel", "swim", "water"],
  // Weather / climate
  "sun": ["weather", "outdoor", "hot", "sunny"],
  "sun-medium": ["sunny", "weather"],
  "sunset": ["weather", "evening"],
  "sunrise": ["weather", "morning"],
  "moon": ["night", "weather", "evening"],
  "snowflake": ["cold", "ac", "air conditioning", "fridge", "winter"],
  "thermometer": ["temperature", "hot", "cold", "ac", "heat"],
  "fan": ["air", "cool", "ventilation", "ac"],
  "air-conditioning": ["ac", "cool", "air"],
  "air-vent": ["ac", "vent", "air"],
  "wind": ["air", "breeze", "fan"],
  "tornado": ["storm", "wind"],
  "cloud": ["weather", "sky"],
  "cloud-rain": ["weather", "rain"],
  "cloud-snow": ["weather", "snow"],
  "droplet": ["water", "drop", "leak"],
  "droplets": ["water", "rain", "leak"],
  "fire": ["flame", "burn", "heat"],
  "flame": ["fire", "burn", "heat", "stove"],
  "flame-off": ["no fire", "extinguish"],
  // Connectivity / electronics
  "wifi": ["internet", "network", "wireless"],
  "wifi-off": ["no internet", "offline"],
  "wifi-high": ["internet", "network", "wireless", "strong"],
  "wifi-slash": ["no internet", "offline"],
  "tv": ["television", "media", "entertainment"],
  "television": ["tv", "media", "entertainment"],
  "television-simple": ["tv", "media", "entertainment"],
  "cable": ["wire", "cord", "tv"],
  "monitor": ["screen", "display", "tv"],
  "airplay": ["cast", "stream", "media"],
  "phone": ["call", "mobile"],
  "plug": ["outlet", "power", "electric"],
  "plug-charging": ["outlet", "power", "electric"],
  "plug-connected": ["outlet", "power", "electric"],
  "plug-x": ["no power", "unplug"],
  "plug-zap": ["power", "electric", "fast"],
  "bolt": ["power", "electric", "lightning"],
  // Maintenance / tools
  "wrench": ["tool", "fix", "repair", "maintenance"],
  "hammer": ["tool", "fix", "repair", "construction"],
  "tool": ["repair", "fix", "maintenance"],
  "tools": ["repair", "fix", "maintenance"],
  "toolbox": ["tools", "repair", "fix"],
  "screwdriver": ["tool", "fix", "repair"],
  "pipe-wrench": ["plumbing", "tool", "fix", "repair"],
  "hard-hat": ["construction", "safety", "worker"],
  "ladder": ["construction", "tool", "climb"],
  "axe": ["tool", "wood", "outdoor"],
  "shovel": ["tool", "garden", "outdoor"],
  "first-aid-kit": ["medical", "safety", "emergency"],
  "candle": ["light", "ambient", "decor"],
  // Status / alerts
  "alert-triangle": ["warning", "danger", "caution"],
  "alert-octagon": ["warning", "danger", "stop"],
  "alert-circle": ["warning", "info", "caution"],
  "warning": ["alert", "danger", "caution"],
  "warning-octagon": ["alert", "danger", "stop"],
  "check": ["done", "ok", "good", "pass"],
  "check-circle": ["done", "ok", "good", "pass"],
  "check-circle-2": ["done", "ok", "good", "pass"],
  "circle-check": ["done", "ok", "good", "pass"],
  "circle-alert": ["warning", "caution"],
  "circle-x": ["fail", "error", "no"],
  "x": ["close", "cancel", "no"],
  "x-circle": ["fail", "error", "no"],
  "ban": ["block", "no", "forbidden"],
  // Pets / safety
  "dog": ["pet", "animal"],
  "paw": ["pet", "animal", "dog", "cat"],
  "bug": ["pest", "insect"],
  "bug-off": ["no pests", "pest control"],
  "bug-beetle": ["pest", "insect"],
  "smoking": ["cigarette", "smoke"],
  "smoking-no": ["no smoking", "no cigarette"],
  "cigarette": ["smoking", "smoke"],
  "cigarette-off": ["no smoking"],
  "cigarette-slash": ["no smoking"],
  // Property / building
  "house": ["home", "building", "property"],
  "house-line": ["home", "building", "property"],
  "house-simple": ["home", "building", "property"],
  "building": ["property", "structure"],
  "building-apartment": ["apartment", "building", "property"],
  "building-cottage": ["cottage", "building", "property"],
  "building-pavilion": ["pavilion", "building", "outdoor"],
  "hotel-service": ["hotel", "bell", "service"],
  "elevator": ["lift", "building"],
  "stairs": ["steps", "building"],
  "window": ["glass", "view"],
  "window-off": ["closed", "no window"],
  "parking": ["car", "park"],
  "accessible": ["disability", "wheelchair", "access"],
  "wheelchair": ["accessible", "disability", "access"],
  "walk": ["walking", "step"],
  "confetti": ["party", "celebration", "welcome"],
  // Misc
  "camera": ["photo", "picture"],
  "image": ["photo", "picture"],
  "mic": ["microphone", "audio"],
  "clipboard": ["list", "notes"],
  "clipboard-check": ["list", "done", "checklist"],
  "clipboard-list": ["list", "notes", "checklist"],
  "clipboard-text": ["list", "notes", "checklist"],
  "list-check": ["list", "checklist", "done"],
  "list-checks": ["list", "checklist", "done"],
  "eye": ["view", "watch", "see"],
  "eye-off": ["hide", "private"],
  "star": ["favorite", "rating"],
  "heart": ["favorite", "like"],
  "bell": ["notify", "alert"],
  "search": ["find", "lookup"],
  "filter": ["narrow", "search"],
  "settings": ["gear", "config", "preferences"],
};

function aliasesFor(id: string): string[] {
  return KEYWORD_ALIASES[id] ?? [];
}

/**
 * Fuzzy scoring used by the picker.
 *
 * Scoring per term (max across name + aliases):
 *   exact match     = 100
 *   prefix match    = 80
 *   substring match = 50
 *   alias substring = 30
 *
 * Multi-word queries require ALL words to score > 0 somewhere on the
 * candidate; total = sum of per-word scores.
 */
function scoreCandidate(name: string, aliases: string[], term: string): number {
  if (!term) return 0;
  if (name === term) return 100;
  if (name.startsWith(term)) return 80;
  if (name.includes(term)) return 50;
  for (const a of aliases) {
    if (a === term) return 60; // alias exact match a bit higher than substring
    if (a.includes(term)) return 30;
  }
  return 0;
}

export function searchIcons(records: IconRecord[] | null | undefined, query: string, cap = 240): IconRecord[] {
  const list = Array.isArray(records) ? records : [];
  const q = (query ?? "").trim().toLowerCase();
  if (!q) return list;
  const terms = q.split(/\s+/).filter(Boolean);
  type Scored = { rec: IconRecord; score: number };
  const out: Scored[] = [];
  for (const rec of list) {
    // Bare id (after pack:) is what aliases are keyed on.
    const parsed = parseKey(rec.key);
    const id = parsed ? parsed.id : rec.key;
    const name = rec.keywords || id.replace(/-/g, " ");
    const aliases = aliasesFor(id);
    let total = 0;
    let allMatched = true;
    for (const t of terms) {
      const s = Math.max(
        scoreCandidate(name, aliases, t),
        scoreCandidate(id, aliases, t),
      );
      if (s === 0) { allMatched = false; break; }
      total += s;
    }
    if (allMatched && total > 0) out.push({ rec, score: total });
  }
  out.sort((a, b) => (b.score - a.score) || a.rec.key.localeCompare(b.rec.key));
  return out.slice(0, cap).map((s) => s.rec);
}
